/** @file Spherical linear interpolation between two rotations along the shorter arc. */
import type { Quat } from './quat';

/** Below this half-angle (radians) between the ends, normalised lerp is used; its error is far below 1e-12. */
const NLERP_BELOW = 1e-4;

/**
 * Rotation at t in [0, 1] from a to b. b is negated when `a . b < 0` (q and -q are one rotation), then
 * `(sin((1 - t) th) a + sin(t th) b) / sin(th)` with `cos(th) = a . b`; the result is renormalised.
 */
export function quatSlerp(a: Quat, b: Quat, t: number): Quat {
  const d0 = a[0] * b[0] + a[1] * b[1] + a[2] * b[2] + a[3] * b[3];
  const sign = d0 < 0 ? -1 : 1;
  const th = Math.acos(Math.min(1, d0 * sign));
  let ka = 1 - t;
  let kb = t;
  if (th > NLERP_BELOW) {
    const s = Math.sin(th);
    ka = Math.sin((1 - t) * th) / s;
    kb = Math.sin(t * th) / s;
  }
  kb *= sign;
  const x = a[0] * ka + b[0] * kb;
  const y = a[1] * ka + b[1] * kb;
  const z = a[2] * ka + b[2] * kb;
  const w = a[3] * ka + b[3] * kb;
  const n = Math.hypot(x, y, z, w);
  return [x / n, y / n, z / n, w / n];
}
